import {
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  signal,
  ViewChild
} from '@angular/core';
import {BehaviorSubject, finalize} from "rxjs";
import {EditSection} from "../../../app/core/enums/section.enum";
import {environment} from "../../../environments/environment";
import {AbstractControl, FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators} from "@angular/forms";
import {ITerritory, UserData, Timer, PatchUserData} from "../../../app/core/interfaces";
import {TimerService} from "../../../app/core/services/timer/timer.service";
import {HttpClient} from "@angular/common/http";
import {UserDataService} from "../../../app/core/services/user-data/user-data.service";
import {MatAutocomplete,MatAutocompleteSelectedEvent,MatAutocompleteTrigger,MatOption} from "@angular/material/autocomplete";
import {ButtonComponent} from "../button/button.component";
import {EditEmailForm, EditPasswordForm, EditPersonalDataForm, EditPhoneForm, EditPhotoForm} from "./interfaces/edit-form.interface";
import {DecimalPipe} from "@angular/common";
import {PhoneMaskDirective} from "../../../app/core/directives/phone-input.directive";
import {PhoneNumberFormatPipe} from "../../../app/core/pipes/phoneHideFormat.pipe";
import {NgxMaskDirective} from "ngx-mask";
import {cleanPhoneNumber, validationMessage } from "../../../app/core/utils";
import {ValidatorDateDirective} from "../../../app/core/directives/validators-datepicker.directive";
import {MatFormField, MatInput} from "@angular/material/input";
import {UserPatchModel} from "../../../app/core/models/patch-user.model";
import {OnlyLettersDirective} from "../../../app/core/directives/only-letters.directive";
import {dateRangeValidator} from "../../../app/core/validators/date-range.validator";
import {emailValidator} from "../../../app/core/validators/email.validator";
import {editField, EditFieldSection} from "../../../app/core/enums/fields.enum";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {NoWhitespaceDirective} from "../../../app/core/directives/no-write-space.directive";

@Component({
  selector: 'edit-fields-form',
  imports: [
    ButtonComponent,
    ReactiveFormsModule,
    PhoneMaskDirective,
    PhoneNumberFormatPipe,
    DecimalPipe,
    NgxMaskDirective,
    ValidatorDateDirective,
    MatOption,
    MatAutocomplete,
    MatInput,
    MatAutocompleteTrigger,
    MatFormField,
    OnlyLettersDirective,
    NoWhitespaceDirective
  ],
  templateUrl: './edit-fields-form.component.html',
  styleUrl: './edit-fields-form.component.scss'
})
export class EditFieldsFormComponent implements OnInit, OnDestroy {

  private timerService = inject(TimerService);
  private http = inject(HttpClient);
  private userDataService = inject(UserDataService);
  private isDataUpdate = false;
  private today = new Date();
  private minDate = new Date(1900, 0, 1);
  private options$ = this.http.get<any>(environment.ApiUrl + '/api/v1.0/dictionary/territory');
  private allRegions: ITerritory[] = [];
  private selectedRegion: ITerritory | null = null;
  private userData: UserData | null = null;
  private currentPhone: string = '';
  private currentEmail: string = '';
  private destroy = inject(DestroyRef)

  public errorMessage = signal<string | null>(null);
  public regionSearchControl = new FormControl<string | null>('', [Validators.required]);
  public filteredRegions: ITerritory[] = [];
  public isFocusedEmailCode: boolean = false;
  public isFocusedEmail: boolean = false;
  public regionPanelIsOpen: boolean = false;
  public timer = this.timerService.timer;
  public timerStatus = this.timerService.timerStatus;
  public sectionENum = EditSection;
  public photoForm: FormGroup<EditPhotoForm> | null = null;
  public  passwordForm: FormGroup<EditPasswordForm> | null = null;
  public personalDataForm: FormGroup<EditPersonalDataForm> | null = null;
  public sectionForFields: { type: EditFieldSection, title: string }[] = [];
  public phoneTouched: boolean = false;
  public phoneFocused = false;
  public phoneInputStarted = false;
  public phoneErrorMessage: string | null = null;
  public isLoading = signal<boolean>(false);
  public isPhoneCodeFocused: boolean = false;
  public currentSection: BehaviorSubject<EditSection> = new BehaviorSubject<EditSection>(
    EditSection.hub
  );
  public arrowFieldState = signal<number| null| boolean >(null)


  public phoneForm: FormGroup<EditPhoneForm> = new FormGroup({
    phoneNumber: new FormControl<string>('', [Validators.required, Validators.pattern(/^\d{11,}$/)]),
    code: new FormControl<string>('', [Validators.required]),
  });

  public emailForm: FormGroup<EditEmailForm> = new FormGroup({
    email: new FormControl<string>('', [Validators.required, Validators.email, Validators.maxLength(50)]),
    code: new FormControl<string>('', [Validators.required]),
  });


  @Input() fields: editField[] = []
  @Input() isCancelWindowOpen: boolean = false;
  @Output() canceledCancelEdit: EventEmitter<void> = new EventEmitter();
  @Output() redirectToBack: EventEmitter<void> = new EventEmitter();
  @Output() editHubIs: EventEmitter<boolean> = new EventEmitter();
  @Output() isCancelText: EventEmitter<boolean> = new EventEmitter();
  @ViewChild('regionInput', {static: false}) regionInputRef!: ElementRef<HTMLInputElement>;

  constructor(private fb: FormBuilder) {
  }

  ngOnInit(): void {
    this.sectionForFields = this.generateSectionList(this.fields);

    if (this.sectionForFields.length > 0) {
      this.getUserData();
    } else {
      setTimeout(() => this.editHubIs.emit(false), 50);
      this.currentSection.next(this.sectionENum.zeroFields);
    }
  }
  ngOnDestroy() {
    this.timerService.stopTimer();
  }

  onCanceledCancelEdit() {
    if (this.currentSection.value === this.sectionENum.hub) {
      this.editHubIs.emit(true);
    } else {
      this.editHubIs.emit(false);
    }
    this.canceledCancelEdit.emit();
  }

  public goToSecetedSection(selectedType: EditFieldSection | undefined): void {
    this.errorMessage.set(null);
    this.phoneErrorMessage = null;

    switch (selectedType) {
      case EditFieldSection.phone:
        this.currentSection.next(this.sectionENum.phone)
        this.editHubIs.emit(false);
        break;
      case EditFieldSection.email:
        this.currentSection.next(this.sectionENum.email)
        this.editHubIs.emit(false);
        break;
      case EditFieldSection.personalData:
        this.currentSection.next(this.sectionENum.personalData)
        this.editHubIs.emit(false);
        break;
      case EditFieldSection.password:
        this.currentSection.next(this.sectionENum.password)
        this.editHubIs.emit(false);
        break;
      case EditFieldSection.photo:
        this.currentSection.next(this.sectionENum.photo)
        this.editHubIs.emit(false);
        break;
      case undefined:
        this.currentSection.next(this.sectionENum.hub);
        this.editHubIs.emit(true);
        break;
    }
  }

  public phoneDisable(): boolean {
    return this.currentPhone === this.phoneForm.controls.phoneNumber.value ||
      !this.phoneForm.get('phoneNumber')?.valid ||
      !!this.phoneErrorMessage;
  }

  public emailDisable(): boolean {
    return this.currentEmail === this.emailForm.controls.email.value ||
      !this.emailForm.get('email')?.valid ||
      !!this.errorMessage();
  }

  private hasField(field: editField): boolean {
    return this.fields.includes(field);
  }

  private getUserData() {
    this.userDataService.getUserData()
      .pipe(takeUntilDestroyed(this.destroy))
      .subscribe({
      next: user => {
        this.userData = user;

        if(user.data.person.birth_date && this.userData){
          this.userData.data.person.birth_date = this.formatDate(user.data.person.birth_date);
        }
        this.initForms(this.fields, user);

        if (this.sectionForFields.length > 1) {
          this.currentSection.next(this.sectionENum.hub);
          setTimeout(() => this.editHubIs.emit(true), 50);
        } else if (this.sectionForFields.length === 1) {
          setTimeout(() => this.editHubIs.emit(false), 50);
          this.goToSecetedSection(this.sectionForFields[0].type);
        }
      },
      error: error => {
        console.error(error);
      }
    })
  }

  public goToHub() {
    this.errorMessage.set(null);
    this.phoneErrorMessage = null;

    if (this.isDataUpdate) {
      this.isDataUpdate = false;
      this.getUserData();
    }
    else {
      this.initForms(this.fields, this.userData);
    }

    this.isCancelText.emit(true);
    this.currentSection.next(this.sectionENum.hub);
    this.editHubIs.emit(true);
  }

  public onPhoneFocus() {
    this.phoneFocused = true;
  }

  public onPhoneBlur() {
    this.phoneFocused = false;
    this.phoneTouched = true;
  }

  public onPhoneInput() {
    this.phoneErrorMessage = null;
    this.phoneInputStarted = true;
  }

  public onPhoneSubmit() {
    if (this.phoneForm.get('phoneNumber')?.invalid) {
      return;
    }
    const phone = this.phoneForm.get('phoneNumber')?.value;
    this.isLoading.set(true);
    this.phoneErrorMessage = null;

    if (!phone) {
      this.phoneErrorMessage = 'Ошибка при отправке номера';
      this.isLoading.set(false);
      return;
    }

    const requestPhone = `+${phone}`
    this.userDataService.addEmailForUserData(requestPhone)
      .pipe(takeUntilDestroyed(this.destroy))
      .subscribe({
      next: () => {
        this.sendPhoneConfirmation(phone);
      },
      error: error => {
        if (error.status === 422) {
          this.sendPhoneConfirmation(phone);
        } else if (error.status === 400) {
          this.phoneErrorMessage =
            error.error?.errors?.by_field?.contact?.[0] ?? 'Ошибка при отправке номера';
          this.isLoading.set(false);
        } else if (error.status === 403 || error.status === 404 || error.status === 409) {
          this.phoneErrorMessage =
            error.error?.errors?.common?.[0] ?? 'Ошибка при отправке номера';
          this.isLoading.set(false);
        } else {
          this.phoneErrorMessage = 'Ошибка при отправке номера';
          this.isLoading.set(false);
        }
      }
    });
  }

  public onPhoneCodeSubmit() {
    if (this.phoneForm.get('code')?.invalid) {
      return;
    }

    this.isLoading.set(true);
    const rawPhoneNumber = this.phoneForm.get('phoneNumber')?.value;
    const phoneNumber = cleanPhoneNumber(rawPhoneNumber!);
    const code = this.phoneForm.get('code')?.value;

    if (!code) {
      this.errorMessage.set('Ошибка при отправке кода');
      this.isLoading.set(false);
      return;
    }

    this.userDataService.confirmPhone(phoneNumber, code)
      .pipe(finalize(() => this.isLoading.set(false)))
      .pipe(takeUntilDestroyed(this.destroy))
      .subscribe({
        next: () => {
          this.phoneForm.reset();
          this.errorMessage.set(null);
          this.isDataUpdate = true;
          this.isCancelText.emit(false);
          this.currentSection.next(this.sectionENum.successSaved);
        },
        error: (error) => {
          if (error.status === 400) {
            this.errorMessage.set(
              error.error?.errors?.by_field?.contact?.[0] ?? 'Неверный код'
            );
          } else {
            this.errorMessage.set(error.error?.errors?.common?.[0] ?? 'Неверный код');
          }
        }
      })
  }

  public onEmailSubmit() {
    if (this.emailForm.get('email')?.invalid) {
      this.errorMessage.set('Email не прошел валидацию');
      return;
    }

    const email = this.emailForm.get('email')?.value;

    this.isLoading.set(true);
    this.errorMessage.set(null);

    if (!email) {
      this.errorMessage.set('Ошибка при отправке номера');
      this.isLoading.set(false);
      return;
    }

    this.userDataService.addEmailForUserData(email)
      .pipe(takeUntilDestroyed(this.destroy))
      .subscribe({
      next: () => {
        this.sendEmailConfirmation(email);
      },
      error: error => {
        if (error.status === 422) {
          this.sendEmailConfirmation(email);
        } else if (error.status === 400) {
          this.errorMessage.set(
            error.error?.errors?.by_field?.contact?.[0] ?? 'Ошибка при отправке почты'
          );
          this.isLoading.set(false);
        } else if (error.status === 403 || error.status === 404 || error.status === 409) {
          this.errorMessage.set(
            error.error?.errors?.common?.[0] ?? 'Ошибка при отправке почты'
          );
          this.isLoading.set(false);
        } else {
          this.errorMessage.set('Ошибка при отправке почты');
          this.isLoading.set(false);
        }
      }
    })
  }

  public onEmailCodeSubmit() {
    if (this.emailForm.get('code')?.invalid) {
      return;
    }

    this.isLoading.set(true);
    const email = this.emailForm.get('email')?.value;
    const code = this.emailForm.get('code')?.value;

    if(!email) {
      this.errorMessage.set('При ожидание кода, почта была утеряна. Введите ее заново');
      this.isLoading.set(false);
      return;
    }

    if (!code) {
      this.errorMessage.set('Ошибка при отправке кода');
      this.isLoading.set(false);
      return;
    }

    this.userDataService.confirmEmail(email, code)
      .pipe(finalize(() => this.isLoading.set(false)))
      .pipe(takeUntilDestroyed(this.destroy))
      .subscribe({
        next: () => {
          this.phoneForm.reset();
          this.errorMessage.set(null);
          this.isDataUpdate = true;
          this.isCancelText.emit(false);
          this.currentSection.next(this.sectionENum.successSaved);
        },
        error: (error) => {
          if (error.status === 400) {
            this.errorMessage.set(
              error.error?.errors?.by_field?.contact?.[0] ?? 'Неверный код'
            );
          } else {
            this.errorMessage.set(error.error?.errors?.common?.[0] ?? 'Неверный код');
          }
        }
      })
  }

  public sendPhoneConfirmation(phone: string | null): void {
    if (!phone) {
      return;
    }

    const requestPhone = `+${phone}`

    this.userDataService.requestForConfirmPhone(requestPhone)
      .pipe(finalize(() => this.isLoading.set(false)))
      .pipe(takeUntilDestroyed(this.destroy))
      .subscribe({
        next: (response) => {
          this.startTimerIfRequired(response);
          this.currentSection.next(this.sectionENum.confirmPhone);
          this.phoneErrorMessage = null;
        },
        error: error => {
          this.startTimerIfRequired(error);

          if (error.status === 400) {
            this.phoneErrorMessage =
              error.error?.errors?.by_field?.contact?.[0] ??
              'Ошибка при отправке кода. Попробуйте ещё раз';
          } else if (
            error.status === 403 ||
            error.status === 404 ||
            error.status === 409 ||
            error.status === 422 ||
            error.status === 429
          ) {
            this.phoneErrorMessage =
              error.error?.errors?.common?.[0] ?? 'Ошибка при отправке кода. Попробуйте ещё раз';
          } else {
            this.phoneErrorMessage = 'Ошибка при отправке кода. Попробуйте ещё раз';
          }
        }
      });
  }

  public sendEmailConfirmation(email: string | null): void {
    if (!email) {
      return;
    }

    this.userDataService.requestForConfirmEmail(email)
      .pipe(finalize(() => this.isLoading.set(false)))
      .pipe(takeUntilDestroyed(this.destroy))
      .subscribe({
          next: (response) => {
            this.startTimerIfRequired(response);
            this.currentSection.next(this.sectionENum.confirmEmail);
            this.errorMessage.set(null);
          },
          error: error => {
            this.startTimerIfRequired(error);

            if (error.status === 400) {
              this.errorMessage.set(
                error.error?.errors?.by_field?.contact?.[0] ??
                'Ошибка при отправке кода. Попробуйте ещё раз'
              );
            } else if (
              error.status === 403 ||
              error.status === 404 ||
              error.status === 409 ||
              error.status === 422 ||
              error.status === 429
            ) {
              this.errorMessage.set(
                error.error?.errors?.common?.[0] ?? 'Ошибка при отправке кода. Попробуйте ещё раз'
              );
            } else {
              this.errorMessage.set('Ошибка при отправке кода. Попробуйте ещё раз');
            }
          }
        }
      )
  }

  public hasFieldError(form: FormGroup, fieldName: string): boolean {
    const control = form.get(fieldName);
    return !!(control && control.invalid && (control.dirty || control.touched));
  }

  private initForms(fields: editField[], userData: UserData | null) {
    const person = userData?.data.person;

    const actualPhone = person?.contacts.phones.find(p => p.is_actual);
    const actualEmail = person?.contacts.emails.find(e => e.is_actual);

    const needPersonalDataForm = fields.includes(editField.name)
      || fields.includes(editField.birthday)
      || fields.includes(editField.region);

    if (fields.includes(editField.phone)) {
      this.currentPhone = (actualPhone?.number ?? '').replace(/^\+/, '');

      this.phoneForm = this.fb.group<EditPhoneForm>({
        phoneNumber: new FormControl(
          (actualPhone?.number ?? '').replace(/^\+/, '')  ?? null,
          [Validators.required, Validators.pattern(/^\d{11,}$/)]
        ),
        code: new FormControl('', Validators.required),
      });
    }

    if (fields.includes(editField.email)) {
      this.currentEmail =  actualEmail?.email  ?? '';

      this.emailForm = this.fb.group<EditEmailForm>({
        email: new FormControl(
          actualEmail?.email ?? null,
          [Validators.required, emailValidator(), Validators.maxLength(50)]
        ),
        code: new FormControl(null, Validators.required),
      });
    }

    if (fields.includes(editField.password)) {
      this.passwordForm = this.fb.group<EditPasswordForm>({
        password: new FormControl(null, [Validators.required, Validators.minLength(6)]),
        repeatPassword: new FormControl(null, Validators.required),
      });
    }

    if (fields.includes(editField.photo)) {
      this.photoForm = this.fb.group<EditPhotoForm>({
        photo: new FormControl(null),
      });
    }

    if (needPersonalDataForm) {
      this.personalDataForm = this.fb.group<EditPersonalDataForm>({
        birth_date: new FormControl(
          person?.birth_date ? this.formatDate(person.birth_date) : null,
          fields.includes(editField.birthday) ? [Validators.required, Validators.minLength(10), dateRangeValidator(this.minDate, this.today)] : []
        ),
        region: new FormControl(
          person?.territory.region ?? null,
          fields.includes(editField.region) ? Validators.required : []
        ),
        first_name: new FormControl(
          person?.name.first ?? null,
          fields.includes(editField.name) ? [Validators.required, Validators.maxLength(30)] : []
        ),
        last_name: new FormControl(
          person?.name.last ?? null,
          fields.includes(editField.name) ? [Validators.required, Validators.maxLength(30)] : []
        ),
        middle_name: new FormControl(
          person?.name.middle ?? null,
          fields.includes(editField.name) ? Validators.maxLength(30) : []
        ),
      });

      if (this.hasField(editField.region)) {
        this.initializeOptionForRegion();
      }

      this.regionSearchControl.setValue(person?.territory.region ?? null);
    }
  }

  private formatDate(apiDate: string): string {
    if (!apiDate) return '';

    if (apiDate.includes('-')) {
      const [year, month, day] = apiDate.split('-');
      return `${day}.${month}.${year}`;
    } else if (apiDate.includes('.')) {
      return apiDate;
    }

    return apiDate;
  }

  private startTimerIfRequired(req: any) {
    let timerValue = parseInt(req.headers.get('Retry-After'));
    if (timerValue) {
      this.timerService.startTimer(timerValue);
    } else {
      this.timerService.stopTimer();
    }
  }

  private generateSectionList(fields: editField[]) {
    const editFieldToSectionMap: Record<editField, EditFieldSection> = {
      [editField.password]: EditFieldSection.password,
      [editField.phone]: EditFieldSection.phone,
      [editField.email]: EditFieldSection.email,
      [editField.photo]: EditFieldSection.photo,
      [editField.name]: EditFieldSection.personalData,
      [editField.region]: EditFieldSection.personalData,
      [editField.birthday]: EditFieldSection.personalData,
    };

    const sectionTitles: Record<EditFieldSection, string> = {
      [EditFieldSection.password]: 'Пароль',
      [EditFieldSection.phone]: 'Номер телефона',
      [EditFieldSection.email]: 'Электронную почту',
      [EditFieldSection.photo]: 'Фото',
      [EditFieldSection.personalData]: 'Основные данные',
    };

    const uniqueSections = new Set<EditFieldSection>();

    fields.forEach(field => {
      const section = editFieldToSectionMap[field];
      uniqueSections.add(section);
    });

    return Array.from(uniqueSections).map(section => ({
      type: section,
      title: sectionTitles[section],
    }));
  }

  private initializeOptionForRegion(): void {
    this.options$
      .pipe(takeUntilDestroyed(this.destroy))
      .subscribe(options => {
      this.allRegions = options?.data?.regions ?? [];
      this.filteredRegions = [...this.allRegions];
    });

    this.regionSearchControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroy))
      .subscribe(searchTerm => {
      const value = typeof searchTerm === 'string' ? searchTerm.toLowerCase() : '';
      this.filteredRegions = this.allRegions.filter(region =>
        region.title.toLowerCase().includes(value)
      );
    });
  }

  public onRegionSelected(event: MatAutocompleteSelectedEvent, controlName: string) {
    const region = event.option.value;
    this.selectedRegion = region;
    this.regionSearchControl.setValue(region);
    this.personalDataForm?.get(`${controlName}`)!.setValue(event.option.value);

    setTimeout(() => {
      this.regionInputRef?.nativeElement.blur();
    });
  }

  public onRegionInputFocus(): void {
    this.regionPanelIsOpen = true;
    this.personalDataForm?.get(`region`)!.setValue(this.regionSearchControl.value)
    this.selectedRegion = null;
    this.regionSearchControl.setValue(null);
  }

  public getErrorTypeFromForm(
    form: FormGroup,
    controlName: string,
    parentGroup?: string,
    grandParent?: string
  ): string | null {
    let control;

    if (grandParent) {
      control = form.get(grandParent)?.get(parentGroup!)?.get(controlName);
    } else if (parentGroup) {
      control = form.get(parentGroup)?.get(controlName);
    } else {
      control = form.get(controlName);
    }

    const errorName = control?.errors ? Object.keys(control.errors)[0] : null;
    if (control?.touched && errorName) {
      return validationMessage(errorName);
    }
    return null;
  }


  public isRequiredFromForm(
    form: FormGroup,
    controlName: string,
    parentGroup?: string,
    grandParent?: string
  ): boolean {
    let control;

    if (grandParent) {
      control = form.get(grandParent)?.get(parentGroup!)?.get(controlName);
    } else if (parentGroup) {
      control = form.get(parentGroup)?.get(controlName);
    } else {
      control = form.get(controlName);
    }
    return !!control?.validator?.({} as AbstractControl)?.['required'];
  }

  public displayRegion(region: ITerritory): string {
    return region?.title ?? '';
  }

  public regionPanelBlur() {
    setTimeout(() => {
      if (!this.selectedRegion) {
        this.regionSearchControl.setValue(this.personalDataForm?.get(`region`)?.value ?? null);
      }

      if (this.regionSearchControl.value?.length === 0 || !this.regionSearchControl.value) {
        this.personalDataForm?.get(`region`)?.setValue(null);
        this.regionSearchControl.setValue(null);
        this.regionSearchControl.markAsTouched();
        this.personalDataForm?.get(`region`)?.markAsTouched();
      }

      setTimeout(() => {
        if (!document.querySelector('.cdk-overlay-pane')) {
          this.regionPanelIsOpen = false;
        }

        const value: string = this.regionSearchControl.value ?? '';
        const match = this.filteredRegions.find(
          (region) => region.title.toLowerCase() === value.toLowerCase());

        if (!match) {
          this.regionSearchControl.setValue(null);
        } else {
          this.regionSearchControl.setValue(match.title);
        }
        this.regionPanelIsOpen = false;
      }, 50);
    }, 150);
  }

  public updatePersonalData() {
    const formValue = this.personalDataForm?.value;

    if (!formValue) {
      return;
    }

    this.isLoading.set(true);

    const patchFormData = {
      fullName: {
        firstName: formValue.first_name,
        lastName: formValue.last_name,
        middleName: formValue.middle_name
      },
      birth_date: formValue.birth_date,
      region: formValue.region
    };

    const bodyModel: PatchUserData = new UserPatchModel(patchFormData);


    this.userDataService.updateUserData(bodyModel)
      .pipe(finalize(() => this.isLoading.set(false)))
      .pipe(takeUntilDestroyed(this.destroy))
      .subscribe({
      next: () => {
        this.errorMessage.set(null);
        this.isDataUpdate = true;
        this.isCancelText.emit(false);
        this.currentSection.next(this.sectionENum.successSaved)
      },
      error: (err) => {
        if (err.status === 400) {
          this.errorMessage.set(
            err.error?.errors?.by_field?.name?.last?.[0] ??
            err.error?.errors?.by_field?.name?.first?.[0] ??
            err.error?.errors?.by_field?.name?.middle?.[0] ??
            err.error?.errors?.by_field?.birth_date?.[0] ??
            err.error?.errors?.by_field?.territory?.okato?.[0] ??
            err.error?.errors?.common?.[0] ??
            'Ошибка при обновлении данных.'
          );
        } else if (err.status === 403) {
          this.errorMessage.set(
            err.error?.errors?.common?.[0] ?? 'Ошибка при обновлении данных.'
          );
        } else {
          this.errorMessage.set('Ошибка при обновлении данных.');
        }
      },
    });
  }

  private isFormChanged(form: FormGroup, userData: UserData): boolean {
    if (!userData || !userData.data?.person) {
      return false;
    }

    const person = userData.data.person;
    const name = person.name;
    const territory = person.territory;

    const map: Record<string, any> = {
      first_name: name.first,
      last_name: name.last,
      middle_name: name.middle,
      birth_date: person.birth_date,
      region: territory.region,
    };

    for (const controlName of Object.keys(form.controls)) {
      const control = form.get(controlName);
      const currentValue = control?.value;
      const originalValue = map[controlName];


      if (controlName === 'region') {
        const currentRegionTitle = currentValue?.title ?? '';
        const originalRegionTitle = originalValue?.title ?? '';
        if (currentRegionTitle.trim() !== originalRegionTitle.trim()) {
          return true;
        }
      } else {
        if ((currentValue ?? '').toString().trim() !== (originalValue ?? '').toString().trim()) {
          return true;
        }
      }
    }

    return false;
  }

  get isPersonalDataSaveDisabled(): boolean {
    return (
      !this.personalDataForm ||
      this.personalDataForm?.invalid ||
      !this.isFormChanged(this.personalDataForm, this.userData!) ||
      !!this.errorMessage()
    );
  }

  get personalDataFieldItems(): { name: string; title: string }[] {
    return this.fields
      .map(f => this.fieldMetaMap[f])
      .filter((meta): meta is { name: string; title: string }[] => Array.isArray(meta))
      .flat();
  }

  private fieldMetaMap: Partial<Record<editField, { name: string; title: string }[]>> = {
    [editField.name]: [
      { name: 'last_name', title: 'Фамилия' },
      { name: 'first_name', title: 'Имя' },
      { name: 'middle_name', title: 'Отчество' }
    ],
    [editField.region]: [{ name: 'region', title: 'Регион' }],
    [editField.birthday]: [{ name: 'birth_date', title: 'Дата рождения' }],
  };

  public goToBackSite() {
    this.redirectToBack.emit();
  }

  public arrowMouseEnter(index?: number) {
      if(index || index === 0) {
        this.arrowFieldState.set(index)
      }else{
        this.arrowFieldState.set(true)
      }
  }
  public arrowMouseLeave(){
    this.arrowFieldState.set(null)
  }
  protected readonly EditFieldSection = EditFieldSection;
  protected readonly event = event;
}
